import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, Moon, Tent } from "lucide-react";
import { Button } from "@/components/ui/button";

const pricingOptions = [
  {
    icon: Home,
    title: "Day Cabana",
    price: "From $99",
    period: "/day",
    description: "10:30 AM – 6:30 PM",
    features: ["Entry for 8 guests", "2 parking spots", "All amenities included"],
    to: "/book-now",
    cta: null as string | null,
  },
  {
    icon: Moon,
    title: "Evening Rental",
    price: "From $49",
    period: "",
    description: "7:30 PM – 10:30 PM",
    features: ["Perfect for sunset gatherings", "Same great amenities", "Star-filled skies"],
    to: "/book-now",
    cta: null as string | null,
  },
  {
    icon: Tent,
    title: "Nearby Glamping",
    price: "From $79",
    period: "/night",
    description: "At our sister properties, 5–13 miles away",
    features: [
      "Creekside cabins & glamping tents",
      "Spring-fed Cibolo & Geronimo Creeks",
      "Pair with your day at the island",
    ],
    to: "/nearby-glamping",
    cta: "Learn More",
  },
];

interface Props {
  id?: string;
}

const PricingPreview = ({ id }: Props = {}) => {
  return (
    <section id={id} style={id ? { scrollMarginTop: "96px" } : undefined} className="py-20 lg:py-28 bg-muted">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Affordable for Groups, Priceless for Memories
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Split among 8 people? That's as low as <span className="text-accent font-semibold">$12.50 per person</span> for a full day.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-12">
          {pricingOptions.map((option, index) => (
            <motion.div
              key={option.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
            >
              <Link
                to={option.to}
                className="block bg-background rounded-2xl p-8 shadow-soft hover:shadow-medium hover:-translate-y-1 transition-all border border-border/50 focus:outline-none focus:ring-2 focus:ring-primary h-full"
              >
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                  <option.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-display text-2xl font-semibold text-foreground mb-2">
                  {option.title}
                </h3>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-3xl font-bold text-primary">{option.price}</span>
                  <span className="text-muted-foreground">{option.period}</span>
                </div>
                <p className="text-sm text-muted-foreground mb-6">{option.description}</p>
                <ul className="space-y-2">
                  {option.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                      <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                      {feature}
                    </li>
                  ))}
                </ul>
                {option.cta && (
                  <div className="mt-6">
                    <Button variant="outline" size="sm" className="w-full pointer-events-none">
                      {option.cta}
                    </Button>
                  </div>
                )}
              </Link>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center"
        >
          <Button variant="hero" size="lg" asChild>
            <Link to="/book-now">View Full Pricing</Link>
          </Button>
          <p className="text-sm text-muted-foreground mt-4">
            Weekdays and off-season = lower prices. Summer weekends = book early!
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingPreview;
